import { encryptedStorage } from './encryptedStorage';
import axios from 'axios';
import { useAuth } from 'context/AuthContext';

export const getDomainId = () => {
    return new Promise((resolve, reject) => {
        try {
            const currentDomain = encryptedStorage.getItem('current_domain');
            if (currentDomain && currentDomain.domain_token) {
                // console.log('Found domain in encrypted storage:', currentDomain.domain_token);
                resolve(currentDomain.domain_token);
                return;
            }
        } catch (error) {
            console.warn('Error reading from encrypted storage:', error);
        }
        let domainId = sessionStorage.getItem('domain_id');
    
        if (!domainId && typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            domainId = params.get('domain_id');
            
            if (domainId) {
                sessionStorage.setItem('domain_id', domainId);
                // console.log('Found domain in URL params, stored in session:', domainId);
                resolve(domainId);
                return;
            }
        }
    
        if (domainId && domainId !== "") {
            //console.log('Found domain in session storage:', domainId);
            resolve(domainId);
            return;
        }
        
        try {
            const userSites = encryptedStorage.getItem('user_sites');
            if (userSites && userSites.length > 0) {
                const firstDomain = userSites[0];
                if (firstDomain.domain_token) {
                    //console.log('Found domain in user sites:', firstDomain.domain_token);
                    sessionStorage.setItem('domain_id', firstDomain.domain_token);
                    resolve(firstDomain.domain_token);
                    return;
                }
            }
        } catch (error) {
            console.warn('Error reading user sites from storage:', error);
        }
        
        console.warn('No domain ID found');
        reject('No domain ID found');
    });
};

export const getDomainIdSync = () => {
    try {
        const currentDomain = encryptedStorage.getItem('current_domain');
        if (currentDomain && currentDomain.domain_token) {
            return currentDomain.domain_token;
        }
    } catch (error) {
        console.warn('Error reading from encrypted storage:', error);
    }

    let domainId = sessionStorage.getItem('domain_id');
    
    if (!domainId && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        domainId = params.get('domain_id');
        
        if (domainId) {
            sessionStorage.setItem('domain_id', domainId);
            return domainId;
        }
    }
    
    if (domainId && domainId !== "") {
        return domainId;
    }
    
    try {
        const userSites = encryptedStorage.getItem('user_sites');
        if (userSites && userSites.length > 0) {
            const firstDomain = userSites[0];
            if (firstDomain.domain_token) {
                sessionStorage.setItem('domain_id', firstDomain.domain_token);
                return firstDomain.domain_token;
            }
        }
    } catch (error) {
        console.warn('Error reading user sites from storage:', error);
    }
    
    return "";
};
export const getDomainHeaders = () => {
    const headers = {};
    
    const secret = sessionStorage.getItem('secret');
    if (secret) {
        headers['SECRET-TOKEN'] = secret;
        return headers;
    }
    
    const domainId = getDomainIdSync();
    if (domainId) {
        headers['DOMAIN-TOKEN'] = domainId;
    }
    
    return headers;
};

export const validateDomainAccess = async () => {
    try {
        const headers = getDomainHeaders();
        
        if (Object.keys(headers).length === 0) {
            return { valid: false, message: 'No domain access credentials found' };
        }
        
        const response = await axios.get(`${process.env.REACT_APP_LAMBDA_BASE_URL}/validate-token`, {
            headers,
            timeout: 5000
        });
        
        if (response.status === 200) {
            return { valid: true, message: 'Domain access valid' };
        } else {
            return { valid: false, message: 'Domain access invalid' };
        }
    } catch (error) {
        console.error('Domain validation error:', error);
        return { 
            valid: false, 
            message: error.response?.status === 401 ? 'Unauthorized domain access' : 'Domain validation failed'
        };
    }
};

export const getCurrentDomainDetails = () => {
    try {
        const currentDomain = encryptedStorage.getItem('current_domain');
        if (currentDomain) {
            return currentDomain;
        }
        
        const userSites = encryptedStorage.getItem('user_sites');
        if (userSites && userSites.length > 0) {
            const domainId = getDomainIdSync();
            const domain = userSites.find(site => site.domain_token === domainId);
            if (domain) {
                return domain;
            }
            return userSites[0];
        }
        
        return null;
    } catch (error) {
        console.warn('Error getting domain details:', error);
        return null;
    }
};

export const switchCurrentDomain = (domainId) => {
    try {
        const userSites = encryptedStorage.getItem('user_sites');
        if (userSites) {
            const domain = userSites.find(site => site.domain_token === domainId);
            if (domain) {
                encryptedStorage.setItem('current_domain', domain);
                
                sessionStorage.setItem('domain_id', domainId);
                
                if (domain.role) {
                    sessionStorage.setItem('role', domain.role);
                }
                
                //console.log('Switched to domain:', domain.domain_name);
                return { success: true, domain };
            }
        }
        return { success: false, message: 'Domain not found' };
    } catch (error) {
        console.error('Error switching domain:', error);
        return { success: false, message: 'Failed to switch domain' };
    }
};

export const useDomain = () => {
    const { currentDomain, sites, switchDomain } = useAuth();
    
    const getDomainId = () => {
        if (currentDomain?.domain_token) {
            return currentDomain.domain_token;
        }
        return getDomainIdSync();
    };
    
    const getDomainHeaders = () => {
        const headers = {};
        const secret = sessionStorage.getItem('secret');
        
        if (secret) {
            headers['SECRET-TOKEN'] = secret;
        } else {
            const domainId = getDomainId();
            if (domainId) {
                headers['DOMAIN-TOKEN'] = domainId;
            }
        }
        
        return headers;
    };
    
    return {

        domainId: getDomainId(),
        getDomainId,
        currentDomain,
        sites,
        
        getDomainHeaders,
        
        switchDomain: (domainId) => {
            const result = switchDomain(domainId);
            if (result) {
                sessionStorage.setItem('domain_id', domainId);
            }
            return result;
        },
        validateAccess: async () => {
            return validateDomainAccess();
        }
    };
};